/**
 * Two-way quote sync — edits made to a quote inside Zoho come back into the app.
 *
 * Each package remembers the Zoho quote it became (zohoId) and Zoho's Modified_Time when the app last
 * wrote or read it (zohoModifiedAt). When a sent inspection is opened, the app reads that inspection's
 * quotes from Zoho ("[C] App 6 - Quotes", action read); any quote changed in Zoho since then replaces
 * the package (items, quantities, prices, descriptions, discount, name) and its PDF is rebuilt.
 * Right before Send, the same check catches a quote edited in Zoho while someone edited it in the app.
 */
import { UNIT_LABEL, getCatalog } from './catalog';
import { callQuotes } from './quoteSend';

export const pullZohoQuotes = async (inspectionId) => {
  if (!inspectionId) return [];
  const out = await callQuotes({ action: 'read', inspectionId });
  return out.quotes || [];
};

const newer = (a, b) => new Date(a).getTime() > new Date(b).getTime();

// "Top-Up Package — Jane Smith" → "Top-Up Package" (the Subject carries the customer's name)
const nameFromSubject = (subject, fallback) => {
  const parts = String(subject || '').split(' — ');
  return (parts.length > 1 ? parts.slice(0, -1).join(' — ') : parts[0]) || fallback || 'Quote';
};

/* A package rebuilt from Zoho's copy of its quote. Lines keep their catalog category/unit when known. */
export const packageFromZoho = (zq, pkg = {}, items = getCatalog().items) => ({
  ...pkg,
  id: pkg.id != null ? pkg.id : Number(zq.key) || zq.key,
  name: nameFromSubject(zq.subject, pkg.name),
  services: zq.items.map((l) => {
    const item = items.find((i) => i.id === l.productId);
    return {
      productId: l.productId,
      name: l.name || (item && item.name) || 'Service',
      category: (item && item.category) || 'Other services',
      subGroup: (item && item.subGroup) || '',
      unit: item ? UNIT_LABEL[item.unit] || 'EA' : 'EA',
      unitRate: Number(l.rate) || 0,
      quantity: Number(l.quantity) || 1,
      description: l.description || (item && item.description) || '',
    };
  }),
  discount: zq.discount ? { name: zq.discountName || 'Discount', type: 'amount', value: zq.discount } : null,
  zohoId: zq.zohoId,
  zohoModifiedAt: zq.modifiedAt,
  quoteNumber: zq.quoteNumber,
});

// what a quote "is", for spotting real differences (ignores names/descriptions the catalog fills in)
const fingerprint = (pkg) =>
  JSON.stringify({
    lines: (pkg.services || []).map((s) => [String(s.productId || s.name), Number(s.quantity) || 1, Number(s.unitRate) || 0]),
    discount: pkg.discount ? Math.round((pkg.discount.type === 'percent' ? -pkg.discount.value : pkg.discount.value) * 100) : 0,
  });

/**
 * Merge Zoho's quotes into the app's packages.
 * A package takes Zoho's version when Zoho changed it after the app last saw it. A package the app has never
 * stamped (sent before two-way sync existed) is only stamped — we can't tell which side is newer.
 * Returns { packages, formats, updated: [names], stamped }.
 */
export const applyZohoQuotes = (packages = [], formats = {}, zohoQuotes = [], items = getCatalog().items) => {
  const updated = [];
  let stamped = false;
  const nextFormats = { ...formats };
  const next = packages.map((pkg) => {
    const zq = zohoQuotes.find((q) => String(q.key) === String(pkg.id));
    if (!zq) return pkg;
    if (!pkg.zohoModifiedAt) {
      stamped = true;
      return { ...pkg, zohoId: zq.zohoId, zohoModifiedAt: zq.modifiedAt, quoteNumber: zq.quoteNumber };
    }
    if (!newer(zq.modifiedAt, pkg.zohoModifiedAt)) return pkg;
    const fromZoho = packageFromZoho(zq, pkg, items);
    if (fingerprint(fromZoho) === fingerprint(pkg) && fromZoho.name === pkg.name) {
      stamped = true;
      return { ...pkg, zohoModifiedAt: zq.modifiedAt }; // only our own save bumped the time
    }
    updated.push(fromZoho.name);
    nextFormats[pkg.id] = zq.format;
    return fromZoho;
  });
  return { packages: next, formats: nextFormats, updated, stamped };
};

/* Packages whose Zoho quote changed after the app's copy — i.e. someone edited it in Zoho meanwhile. */
export const zohoConflicts = (packages = [], zohoQuotes = []) =>
  packages.filter((pkg) => {
    const zq = zohoQuotes.find((q) => String(q.key) === String(pkg.id));
    return zq && pkg.zohoModifiedAt && newer(zq.modifiedAt, pkg.zohoModifiedAt);
  });

/* After a save: stamp each package with its Zoho quote, so the next open knows what's new. */
export const stampPackages = (packages = [], saved = []) =>
  packages.map((pkg) => {
    const q = saved.find((s) => String(s.key) === String(pkg.id));
    return q ? { ...pkg, zohoId: q.zohoId, zohoModifiedAt: q.modifiedAt || pkg.zohoModifiedAt, quoteNumber: q.quoteNumber } : pkg;
  });
